import { createReducer, getType } from "typesafe-actions";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";

import resourceFieldModel, { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import { createEmptyFilterModel } from "@app/util/filters/utils";

import {
  Actions,
  bulkDeleteFolders,
  createFolder,
  deleteFolder,
  fetchFolders,
  fetchPinnedResources,
  fetchResources,
  fetchTrashResourcesCount,
  initialize,
  pinUnpinResource,
  updateFolder,
  uploadFiles,
} from "./actions";
import externalResourcesReducer, {
  ExternalResourcesActions,
  ExternalResourcesState,
  initialExternalResourcesState,
} from "./externalResources";
import ResourcesViewState from "./ResourcesViewState";

const DEFAULT_PAGE_SIZE = 10;

interface ResourcesState {
  deletedResourcesCount?: number;
  externalResources: ExternalResourcesState;
  filesUploading: boolean;
  folders: Folder[];
  initializing: boolean;
  pinnedResources: Resource[];
  pinnedUpdating: boolean;
  resources: Resource[];
  resourcesLoading: boolean;
  storageUsed: number;
  total: number;
  viewState: ResourcesViewState;
}

const initialState: ResourcesState = {
  deletedResourcesCount: 0,
  externalResources: initialExternalResourcesState,
  filesUploading: false,
  folders: [],
  initializing: false,
  pinnedResources: [],
  pinnedUpdating: false,
  resources: [],
  resourcesLoading: false,
  storageUsed: 0,
  total: 0,
  viewState: {
    columns: [],
    filter: createEmptyFilterModel(),
    range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
    sort: [
      { field: resourceFieldModel.getByName(ResourceFieldName.CREATED_AT)!, order: SortOrder.DESC },
    ],
  },
};

const resources = createReducer<ResourcesState, Actions>(initialState)
  .handleType(getType(initialize.request), (state) => ({
    ...state,
    initializing: true,
  }))
  .handleType(getType(initialize.success), (state, { payload }) => ({
    ...state,
    folders: payload.folders,
    initializing: false,
    resources: payload.resources,
    storageUsed: payload.storageUsed,
  }))
  .handleType(getType(initialize.failure), (state) => ({
    ...state,
    initializing: false,
  }))
  .handleType(getType(fetchResources.request), (state, { payload }) => ({
    ...state,
    resourcesLoading: true,
    viewState: {
      ...state.viewState,
      ...payload,
      filter: payload.filter ? payload.filter : state.viewState.filter,
    },
  }))
  .handleType(
    getType(fetchResources.success),
    (state, { payload: { resources, storageUsed, totalResources } }) => ({
      ...state,
      resources,
      resourcesLoading: false,
      storageUsed,
      total: totalResources,
      viewState: { ...state.viewState },
    })
  )
  .handleType(getType(fetchResources.failure), (state) => ({
    ...state,
    resourcesLoading: false,
  }))
  .handleType(getType(uploadFiles.request), (state) => ({
    ...state,
    filesUploading: true,
  }))
  .handleType(getType(uploadFiles.success), (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleType(getType(uploadFiles.failure), (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleType(getType(fetchFolders.success), (state, { payload }) => ({
    ...state,
    folders: payload,
  }))
  .handleType(getType(createFolder.success), (state, { payload }) => ({
    ...state,
    folders: [...state.folders, payload],
  }))
  .handleType(getType(updateFolder.success), (state, { payload }) => ({
    ...state,
    folders: state.folders.map((folder) => (folder.id === payload.id ? payload : folder)),
  }))
  .handleType(getType(bulkDeleteFolders.success), (state, { payload }) => ({
    ...state,
    folders: state.folders.filter(({ id }) => !payload.includes(id)),
  }))
  .handleType(getType(deleteFolder.success), (state, { payload }) => ({
    ...state,
    folders: state.folders.filter(({ id }) => id !== payload),
  }))
  .handleType(getType(fetchPinnedResources.success), (state, { payload }) => ({
    ...state,
    pinnedResources: payload,
  }))
  .handleType(getType(pinUnpinResource.request), (state) => ({
    ...state,
    pinnedUpdating: true,
  }))
  .handleType(getType(pinUnpinResource.success), (state, { payload }) => ({
    ...state,
    pinnedResources: payload,
    pinnedUpdating: false,
  }))
  .handleType(getType(pinUnpinResource.failure), (state) => ({
    ...state,
    pinnedUpdating: false,
  }))
  .handleType(getType(fetchTrashResourcesCount.success), (state, { payload }) => ({
    ...state,
    deletedResourcesCount: payload,
  }))
  .handleType(
    Object.keys(externalResourcesReducer.handlers) as ExternalResourcesActions["type"][],
    (state: ResourcesState, action: ExternalResourcesActions) => ({
      ...state,
      externalResources: externalResourcesReducer(state.externalResources, action),
    })
  );

export * from "./selectors";
export type ResourcesSceneActions = Actions;
export default resources;
